import { postNdjsonStream, ApiError } from "./client";
import type { StreamEvent } from "@career-ops-amplify/shared";

export type RunEventCallback = (event: StreamEvent) => void;

/**
 * POST to the run/evaluation endpoint and stream NDJSON events back.
 * Returns the total token/cost summary when the stream completes.
 */
export async function runEvaluation(
  kind: string,
  input: string,
  onEvent: RunEventCallback,
  signal?: AbortSignal,
): Promise<{ tokens: number; costUsd: number | null } | null> {
  let summary: { tokens: number; costUsd: number | null } | null = null;

  await postNdjsonStream(
    "/api/run",
    { kind, input },
    (raw) => {
      const event = raw as StreamEvent;
      if (event.type === "done") {
        summary = { tokens: event.tokens, costUsd: event.costUsd ?? null };
      }
      onEvent(event);
    },
    signal,
  );

  return summary;
}

/**
 * Parse an NDJSON response stream manually (for cases where the response
 * is obtained outside of `runEvaluation`).
 */
export async function parseNdjsonStream(
  stream: ReadableStream<Uint8Array>,
  onEvent: RunEventCallback,
): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          const parsed = JSON.parse(trimmed) as StreamEvent;
          onEvent(parsed);
        } catch {
          // skip malformed lines
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}